import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

/**
 * An always-visible horizontal scrollbar for a container that scrolls wider
 * than the screen.
 *
 * A browser's own horizontal bar is invisible until the cursor happens to
 * hover the bottom edge — and on macOS, with the default "show scroll bars
 * when scrolling" setting, invisible full stop until the fingers move. A
 * table that always continues off the right edge needs a standing cue that
 * it does, so this draws one.
 *
 * Drawing it is only half the job. A bar that looks like a scrollbar and
 * does not drag reads as broken, so this one is a real control: the thumb
 * drags, the track takes a click, and the whole thing takes the keyboard
 * under `role="scrollbar"`. The scroll container it drives wears
 * `scrollbar-none` so the platform's own overlay bar does not appear
 * alongside it mid-scroll.
 *
 * Geometry is measured in pixels rather than percentages because a drag has
 * to map pointer travel onto `scrollLeft` exactly; the thumb also has a
 * floor width, so a very wide table still leaves something grabbable.
 */

/** Below this the thumb is too small to aim at with a mouse. */
const MIN_THUMB_PX = 32;

/** Fraction of a viewport width an arrow key moves. */
const ARROW_STEP = 0.15;

/** Fraction of a viewport width Page Up/Down moves. */
const PAGE_STEP = 0.9;

interface Metrics {
  thumbPx: number;
  offsetPx: number;
  /** Whether the container overflows at all — the bar hides entirely when it doesn't. */
  scrollable: boolean;
  /** 0–100, for `aria-valuenow`. */
  valueNow: number;
}

const IDLE: Metrics = { thumbPx: 0, offsetPx: 0, scrollable: false, valueNow: 0 };

interface HorizontalScrollbarProps {
  /** The scroll container this bar drives. */
  targetRef: RefObject<HTMLElement | null>;
  /** `id` of that container, for `aria-controls`. */
  controlsId: string;
  label: string;
}

export function HorizontalScrollbar({
  targetRef,
  controlsId,
  label,
}: HorizontalScrollbarProps): React.JSX.Element | null {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [metrics, setMetrics] = useState<Metrics>(IDLE);
  /** Set while the thumb is being dragged; cleared on pointer up. */
  const drag = useRef<{ pointerId: number; startX: number; startScrollLeft: number } | null>(null);

  const measure = useCallback(() => {
    const el = targetRef.current;
    if (el === null) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    // Whether the bar exists at all is the container's business alone. The
    // track cannot be measured until it is on screen, and it is only on
    // screen once this says so.
    if (maxScroll <= 1) {
      setMetrics(IDLE);
      return;
    }
    const trackPx = trackRef.current?.clientWidth ?? 0;
    const thumbPx =
      trackPx === 0
        ? 0
        : Math.min(trackPx, Math.max(MIN_THUMB_PX, (clientWidth / scrollWidth) * trackPx));
    const progress = Math.min(1, Math.max(0, scrollLeft / maxScroll));
    setMetrics({
      thumbPx,
      offsetPx: progress * (trackPx - thumbPx),
      scrollable: true,
      valueNow: Math.round(progress * 100),
    });
  }, [targetRef]);

  /*
   * The bar listens to the container rather than taking an `onScroll` prop:
   * it also has to re-measure when the *content* changes width, which fires
   * no scroll event at all and — since rows arrive from a live query long
   * after mount — no window resize either. A `ResizeObserver` on both the
   * container and the table inside it catches every case that moves the
   * thumb without anyone scrolling.
   */
  useEffect(() => {
    const el = targetRef.current;
    if (el === null) {
      return;
    }
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observerRef.current = observer;
    observer.observe(el);
    const content = el.firstElementChild;
    if (content !== null) {
      observer.observe(content);
    }
    if (trackRef.current !== null) {
      observer.observe(trackRef.current);
    }
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
      observerRef.current = null;
    };
  }, [targetRef, measure]);

  /*
   * A callback ref, not a plain one: the track only mounts once the
   * container is known to overflow, which is after the effect above has
   * already built the observer. This hands it over whenever that happens
   * and re-measures now that there is a width to read.
   */
  const setTrackEl = useCallback(
    (el: HTMLDivElement | null) => {
      const previous = trackRef.current;
      if (previous !== null) {
        observerRef.current?.unobserve(previous);
      }
      trackRef.current = el;
      if (el !== null) {
        observerRef.current?.observe(el);
        measure();
      }
    },
    [measure],
  );

  const scrollBy = useCallback(
    (deltaPx: number) => {
      const el = targetRef.current;
      if (el === null) {
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      el.scrollLeft = Math.min(maxScroll, Math.max(0, el.scrollLeft + deltaPx));
    },
    [targetRef],
  );

  /** Puts the thumb's centre under the pointer — a click anywhere on the track. */
  const jumpTo = useCallback(
    (clientX: number) => {
      const el = targetRef.current;
      const track = trackRef.current;
      if (el === null || track === null) {
        return;
      }
      const rect = track.getBoundingClientRect();
      const travel = rect.width - metrics.thumbPx;
      if (travel <= 0) {
        return;
      }
      const progress = Math.min(
        1,
        Math.max(0, (clientX - rect.left - metrics.thumbPx / 2) / travel),
      );
      el.scrollLeft = progress * (el.scrollWidth - el.clientWidth);
    },
    [targetRef, metrics.thumbPx],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const el = targetRef.current;
      const track = trackRef.current;
      if (el === null || track === null || !metrics.scrollable || event.button !== 0) {
        return;
      }
      // Stops the press selecting the table text it started over.
      event.preventDefault();
      const rect = track.getBoundingClientRect();
      const thumbLeft = rect.left + metrics.offsetPx;
      // A press off the thumb jumps there first, so the drag that follows
      // continues from under the pointer rather than snapping back.
      if (event.clientX < thumbLeft || event.clientX > thumbLeft + metrics.thumbPx) {
        jumpTo(event.clientX);
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: el.scrollLeft,
      };
      event.currentTarget.focus();
    },
    [targetRef, metrics.scrollable, metrics.offsetPx, metrics.thumbPx, jumpTo],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const el = targetRef.current;
      const track = trackRef.current;
      const active = drag.current;
      if (el === null || track === null || active?.pointerId !== event.pointerId) {
        return;
      }
      const travel = track.clientWidth - metrics.thumbPx;
      if (travel <= 0) {
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      const moved = ((event.clientX - active.startX) / travel) * maxScroll;
      el.scrollLeft = Math.min(maxScroll, Math.max(0, active.startScrollLeft + moved));
    },
    [targetRef, metrics.thumbPx],
  );

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) {
      return;
    }
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const el = targetRef.current;
      if (el === null) {
        return;
      }
      const page = el.clientWidth;
      switch (event.key) {
        case 'ArrowLeft':
          scrollBy(-page * ARROW_STEP);
          break;
        case 'ArrowRight':
          scrollBy(page * ARROW_STEP);
          break;
        case 'PageUp':
          scrollBy(-page * PAGE_STEP);
          break;
        case 'PageDown':
          scrollBy(page * PAGE_STEP);
          break;
        case 'Home':
          el.scrollLeft = 0;
          break;
        case 'End':
          el.scrollLeft = el.scrollWidth - el.clientWidth;
          break;
        default:
          return;
      }
      event.preventDefault();
    },
    [targetRef, scrollBy],
  );

  if (!metrics.scrollable) {
    return null;
  }

  return (
    <div
      role="scrollbar"
      aria-label={label}
      aria-controls={controlsId}
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={metrics.valueNow}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      data-testid="horizontal-scrollbar"
      className="border-border bg-surface-sunken focus-visible:outline-ring touch-none border-t px-2 py-1.5 outline-none select-none focus-visible:outline-2 focus-visible:-outline-offset-2"
    >
      <div
        ref={setTrackEl}
        className="bg-border-strong/30 relative h-1.5 w-full cursor-pointer overflow-hidden rounded-full"
      >
        <div
          data-testid="horizontal-scrollbar-thumb"
          className="bg-accent absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${String(metrics.thumbPx)}px`,
            transform: `translateX(${String(metrics.offsetPx)}px)`,
          }}
        />
      </div>
    </div>
  );
}
